import { Alert, Input, InputNumber, Segmented } from 'antd';
import { NUMBER_LIMITS } from '../../utils/elementValidation';
import { MAX_STEP, MAX_DECIMALS, stepFits } from '../../utils/elementFormat/number';
import { showsAsAmount, sliderAvailable, stepDecimalsFor } from '../../config/elementOptionPolicy';
import { NO_TEXT_ASSIST } from '../../utils/fieldAssist';

/**
 * The card-definition sub-editor for a `number` element's options — the four
 * that take a value (docs/plans/data-types.md decision 3; "Disallow
 * negatives" is a boolean and rides the toggle run above with every other
 * switch, from ELEMENT_OPTION_TOGGLES):
 *
 *   decimals      0–6 fixed places. Rounds on WRITE (the API), and the item
 *                 form's input takes the same precision, so "2" means every
 *                 stored value has two. Absent = unconstrained.
 *   leadingZeros  2–20 digits to pad the integer part to, for the ids people
 *                 keep in numbers ("007"). DISPLAY only — the stored value is
 *                 still 7 — and the sign leads the padding (-007).
 *   min / max     the bounds the API refuses values outside of, and the
 *                 bounds the item form's input clamps to.
 *
 *   prefix        literal text in front of the rendered value ("WO-000417"),
 *                 up to 16 characters. DISPLAY only, like leadingZeros — an
 *                 identifier people read, never part of what is stored,
 *                 sorted or compared. Offered on an ordinary number too:
 *                 PO-1234 typed by hand is the same rendering problem as a
 *                 server-allocated one, and the platform spells it one way.
 *
 * 'integer' is the legacy spelling of a whole-number element, so its decimals
 * are pinned at 0 rather than offered: the field would be a lie the input and
 * the API both ignore. SERVER NUMBERING pins them the same way and for a
 * harder reason — a counter issues whole numbers, and the API refuses the
 * pairing — and it takes over the prefix and the padding entirely, because
 * on a numbered element those two ARE the numbering format and belong beside
 * "start at" rather than three fields away (see SequenceOptions.js).
 *
 * PR 7's number polish (docs/plans/data-types-second-pass.md §3.8):
 *
 *   step          what − / +, antd's arrows and the slider move by: a
 *                 positive number up to 1,000,000 that fits the decimals
 *                 (utils/elementFormat/number.js stepFits). Absent = 1.
 *   control       Show as Number or Slider. Slider is offered only with a
 *                 literal minimum below a literal maximum — its two ends.
 *
 * "Show as a fraction" (KPR-1025) is a switch in the same run, withheld
 * beside an identifier, a percent or decimals 0 or 1; this editor says why
 * when decimals are set below 2 after it is on.
 *
 * "Show as a percent" and "Group thousands" are switches in the toggle run
 * (ELEMENT_OPTION_TOGGLES). An identifier is never either, so while one is
 * on, Leading zeros and Prefix are withheld here (kept while set, so a
 * stored pair can be cleared) — and the toggle run withholds the two
 * switches while padding or a prefix is set. Whichever came first keeps
 * the field; the API refuses the pair either way.
 *
 * Writes go through the form's path-based onChange (`elements[i].options.x`)
 * so this editor holds no state of its own; clearing a field writes
 * `undefined` so the key serializes away rather than storing a null the API
 * would have to read as "off".
 */

const finiteOrUndefined = v => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);

const intOrUndefined = v => (typeof v === 'number' && Number.isInteger(v) ? v : undefined);

// The Step field's one line: what it does, or why the step will not save.
const stepProblemText = (problem, decimals) => {
	if (!problem) return 'What − / + and the slider move by.';
	if (decimals === 0) return 'Use a whole number — this element keeps no decimals.';
	const places = decimals ?? MAX_DECIMALS;
	return `Use at most ${places} digit${places === 1 ? '' : 's'} after the point — this element keeps no more.`;
};

// The Show as field's one line.
const sliderHint = (isSlider, canSlide) => {
	if (canSlide) return 'A slider runs from the minimum to the maximum, with the number beside it.';
	return isSlider
		? 'A slider needs a minimum below the maximum. Set both, or show a number.'
		: 'Set a minimum and a maximum to show a slider.';
};

export default function NumberOptions({ index, element, onChange, basePath = null }) {
	const options = element?.options || {};
	const prefix = `${basePath || `elements[${index}]`}.options`;
	const isInteger = element?.dataType === 'integer';
	// Server-numbered: the sub-editor below owns the spelling, and a counter
	// has no decimals to choose.
	const isSequenced = !!options.sequence;
	const decimalsPinned = isInteger || isSequenced;
	// A manual order (KPR-1396): keepr writes the value between two others, so
	// decimals (which would round it), bounds (which would refuse it) and the
	// steppers (nobody types it) step aside. The API refuses each pairing.
	const isRanked = options.rank === true;

	const min = finiteOrUndefined(options.min);
	const max = finiteOrUndefined(options.max);
	const boundsCross = min !== undefined && max !== undefined && min > max;

	const set = (key, value) => onChange(`${prefix}.${key}`, value);

	// PR 7. An identifier field already set keeps showing, so it can be
	// cleared; an unset one steps aside while the value shows as an amount.
	const amount = showsAsAmount(options);
	const showLeadingZeros = !amount || intOrUndefined(options.leadingZeros) !== undefined;
	const showPrefix = !amount || (typeof options.prefix === 'string' && options.prefix !== '');
	const step = finiteOrUndefined(options.step);
	const stepDecimals = stepDecimalsFor(element?.dataType, options);
	const stepProblem = step !== undefined && !stepFits(step, stepDecimals);
	const isSlider = options.control === 'slider';
	// KPR-1025: a fraction is only near a value kept at 2 decimals or more
	// (the API refuses the pair; this says why before the save does).
	const fractionsTooCoarse = options.fractions === true && Number.isInteger(options.decimals) && options.decimals < 2;
	const canSlide = sliderAvailable(options);

	return (
		<div className='number-options'>
			<div className='micro-label card-def-element__section'>Number</div>

			{fractionsTooCoarse ? (
				<Alert
					type='error'
					showIcon
					className='mb-2'
					message={`Show as a fraction needs at least 2 decimals`}
					description={`At ${options.decimals} decimal${options.decimals === 1 ? '' : 's'} a value such as 1/8 is stored as ${options.decimals === 0 ? '0' : '0.1'}, which no fraction is near. Keep 2 or more, clear Decimals, or turn the switch off.`}
				/>
			) : null}

			{boundsCross ? (
				<Alert
					type='error'
					showIcon
					className='mb-2'
					message='The minimum is above the maximum'
					description='No value could satisfy both, so nothing would ever save. Swap them, or clear one.'
				/>
			) : null}

			{isRanked ? (
				<div className='form-panel__hint mb-2'>A manual order is set by moving items in a list sorted by this field, so it takes no decimals, bounds or step.</div>
			) : null}

			<div className='number-options__row' hidden={isRanked}>
				<div className='mb-2 number-options__cell'>
					<label className='d-block' htmlFor={`${prefix}.decimals`}>Decimals</label>
					<InputNumber
						id={`${prefix}.decimals`}
						size='large'
						className='w-100'
						min={0}
						max={NUMBER_LIMITS.MAX_DECIMALS}
						step={1}
						precision={0}
						disabled={decimalsPinned}
						value={decimalsPinned ? 0 : (intOrUndefined(options.decimals) ?? null)}
						onChange={v => set('decimals', intOrUndefined(v))}
						placeholder='Any'
						inputMode='numeric'
						aria-label='Decimals'
					/>
					{isSequenced ? (
						<div className='form-panel__hint'>A numbered element counts, so it has none.</div>
					) : isInteger ? (
						<div className='form-panel__hint'>A whole-number element always has none.</div>
					) : null}
				</div>
				{/* Padding and prefix move to the Numbering panel while the
				    server owns the value: two places to set one spelling is
				    how the two get to disagree. */}
				{isSequenced || !showLeadingZeros ? null : (
					<div className='mb-2 number-options__cell'>
						<label className='d-block' htmlFor={`${prefix}.leadingZeros`}>Leading zeros</label>
						<InputNumber
							id={`${prefix}.leadingZeros`}
							size='large'
							className='w-100'
							min={NUMBER_LIMITS.MIN_LEADING_ZEROS}
							max={NUMBER_LIMITS.MAX_LEADING_ZEROS}
							step={1}
							precision={0}
							value={intOrUndefined(options.leadingZeros) ?? null}
							onChange={v => set('leadingZeros', intOrUndefined(v))}
							placeholder='Off'
							inputMode='numeric'
							aria-label='Leading zeros'
						/>
						<div className='form-panel__hint'>Digits to pad to when showing the value — 3 shows 7 as 007.</div>
					</div>
				)}
			</div>

			{amount && !isSequenced && (!showLeadingZeros || !showPrefix) ? (
				<div className='form-panel__hint mb-2'>Leading zeros and a prefix are for identifiers, so they are off while the value shows as a percent, grouped or as a fraction.</div>
			) : null}

			{isSequenced || !showPrefix ? null : (
				<div className='mb-2'>
					<label className='d-block' htmlFor={`${prefix}.prefix`}>Prefix</label>
					<Input
						{...NO_TEXT_ASSIST}
						id={`${prefix}.prefix`}
						size='large'
						maxLength={NUMBER_LIMITS.MAX_PREFIX}
						value={typeof options.prefix === 'string' ? options.prefix : ''}
						onChange={e => set('prefix', e.target.value ? e.target.value : undefined)}
						placeholder='None'
						aria-label='Prefix'
					/>
					<div className='form-panel__hint'>Shown in front of the value — "PO-" shows 1234 as PO-1234. Not stored, and never sorted or compared on.</div>
				</div>
			)}

			<div className='number-options__row' hidden={isRanked}>
				<div className='mb-2 number-options__cell'>
					<label className='d-block' htmlFor={`${prefix}.min`}>Minimum</label>
					<InputNumber
						id={`${prefix}.min`}
						size='large'
						className='w-100'
						value={min ?? null}
						onChange={v => set('min', finiteOrUndefined(v))}
						status={boundsCross ? 'error' : undefined}
						controls={false}
						inputMode='decimal'
						aria-label='Minimum'
					/>
				</div>
				<div className='mb-2 number-options__cell'>
					<label className='d-block' htmlFor={`${prefix}.max`}>Maximum</label>
					<InputNumber
						id={`${prefix}.max`}
						size='large'
						className='w-100'
						value={max ?? null}
						onChange={v => set('max', finiteOrUndefined(v))}
						status={boundsCross ? 'error' : undefined}
						controls={false}
						inputMode='decimal'
						aria-label='Maximum'
					/>
				</div>
			</div>

			{/* PR 7: what the steppers and the slider move by, and the slider
			    itself. A numbered element is never typed, so neither applies. */}
			{isSequenced || isRanked ? null : (
				<div className='number-options__row'>
					<div className='mb-2 number-options__cell'>
						<label className='d-block' htmlFor={`${prefix}.step`}>Step</label>
						<InputNumber
							id={`${prefix}.step`}
							size='large'
							className='w-100'
							min={0}
							max={MAX_STEP}
							value={step ?? null}
							onChange={v => set('step', finiteOrUndefined(v) && v > 0 ? v : undefined)}
							status={stepProblem ? 'error' : undefined}
							placeholder='1'
							controls={false}
							inputMode='decimal'
							aria-label='Step'
						/>
						<div className='form-panel__hint'>
							{stepProblemText(stepProblem, stepDecimals)}
						</div>
					</div>
					<div className='mb-2 number-options__cell'>
						<label className='d-block' htmlFor={`${prefix}.control`}>Show as</label>
						<Segmented
							id={`${prefix}.control`}
							size='large'
							block
							value={isSlider ? 'slider' : 'input'}
							onChange={v => set('control', v === 'slider' ? 'slider' : undefined)}
							options={[
								{ label: 'Number', value: 'input' },
								{ label: 'Slider', value: 'slider', disabled: !canSlide && !isSlider },
							]}
							aria-label='Show as'
						/>
						<div className='form-panel__hint'>
							{sliderHint(isSlider, canSlide)}
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
